import { type HTMLAttributes } from 'react';
import { UserIcon } from '@heroicons/react/24/solid';

interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  status?: 'online' | 'offline' | 'away' | 'busy';
  variant?: 'auto' | 'brand'; // 'auto' uses hash-based colors, 'brand' uses teal
}

const sizeStyles = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
  xl: 'w-16 h-16 text-xl',
};

const iconSizes = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8',
};

const statusSizes = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
  xl: 'w-4 h-4',
};

const statusColors = {
  online: 'bg-success-500',
  offline: 'bg-surface-400',
  away: 'bg-warning-500',
  busy: 'bg-danger-500',
};

// Get initials from name
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Generate consistent color from name
function getColorFromName(name: string): string {
  const colors = [
    'bg-primary-500',
    'bg-accent-500',
    'bg-rose-500',
    'bg-amber-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-cyan-500',
    'bg-fuchsia-500',
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export function Avatar({
  src,
  alt,
  name,
  size = 'md',
  status,
  variant = 'auto',
  className = '',
  ...props
}: AvatarProps) {
  const showImage = src;
  const showInitials = !src && name;
  const showFallback = !src && !name;

  // Determine background color
  const bgColor = showInitials
    ? variant === 'brand'
      ? 'bg-teal-500 dark:bg-teal-600'
      : getColorFromName(name!)
    : 'bg-surface-200 dark:bg-surface-700';

  return (
    <div className={`relative inline-flex ${className}`} {...props}>
      <div
        className={`
          ${sizeStyles[size]}
          rounded-full overflow-hidden
          flex items-center justify-center
          ring-2 ring-white dark:ring-surface-800
          ${bgColor}
        `}
      >
        {showImage && (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className="w-full h-full object-cover"
          />
        )}

        {showInitials && (
          <span className="font-semibold text-white select-none">
            {getInitials(name!)}
          </span>
        )}

        {showFallback && (
          <UserIcon className={`${iconSizes[size]} text-surface-400 dark:text-surface-500`} />
        )}
      </div>

      {/* Status indicator */}
      {status && (
        <span
          className={`
            absolute bottom-0 right-0
            ${statusSizes[size]} rounded-full
            ${statusColors[status]}
            ring-2 ring-white dark:ring-surface-800
          `}
        />
      )}
    </div>
  );
}

// Avatar group for stacking multiple avatars
interface AvatarGroupProps {
  avatars: Array<{
    src?: string;
    name?: string;
  }>;
  max?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export function AvatarGroup({
  avatars,
  max = 4,
  size = 'md',
  className = '',
}: AvatarGroupProps) {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <div className={`flex -space-x-2 ${className}`}>
      {visibleAvatars.map((avatar, index) => (
        <Avatar
          key={index}
          src={avatar.src}
          name={avatar.name}
          size={size}
        />
      ))}
      {remainingCount > 0 && (
        <div
          className={`
            ${sizeStyles[size]}
            rounded-full
            flex items-center justify-center
            bg-surface-200 dark:bg-surface-700
            ring-2 ring-white dark:ring-surface-800
            font-medium text-surface-600 dark:text-surface-300
          `}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
}

export default Avatar;
